// Scheduling module: public booking links (Calendly-style) + bookings.
// The weekly availability template is expressed in the LINK's timezone;
// slots returned by the public API are UTC ISO strings that the booking page
// renders in the invitee's timezone.

export type SchedulingLocationType = 'VIDEO' | 'PHONE' | 'CUSTOM'

export type SchedulingVideoProvider =
  | 'AUTO'
  | 'GOOGLE_MEET'
  | 'MICROSOFT_TEAMS'
  | 'ZOOM'

export type SchedulingBookingStatus = 'CONFIRMED' | 'CANCELLED'

export const SCHEDULING_WEEKDAYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const

export type SchedulingWeekday = (typeof SCHEDULING_WEEKDAYS)[number]

/** One bookable window inside a day, 24h 'HH:mm' in the link's timezone */
export interface SchedulingAvailabilityInterval {
  start: string
  end: string
}

/** Weekly template: absent day = unavailable */
export type SchedulingAvailabilityTemplate = Partial<
  Record<SchedulingWeekday, SchedulingAvailabilityInterval[]>
>

/**
 * Date-specific overrides on an availability schedule (Calendly-style):
 * key is a 'YYYY-MM-DD' LOCAL date in the schedule's timezone; the intervals
 * replace the weekly template for that date, and an EMPTY array means
 * unavailable all day. Overrides only exist on schedules — links with inline
 * availability pick them up by attaching a schedule.
 */
export type SchedulingDateOverrides = Record<
  string,
  SchedulingAvailabilityInterval[]
>

const SCHEDULING_HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/
const SCHEDULING_DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/

/** 'HH:mm' interval with start < end — shared by weekly + override shapes */
export function isValidSchedulingInterval(
  value: unknown,
): value is SchedulingAvailabilityInterval {
  const { start, end } = (value ?? {}) as SchedulingAvailabilityInterval
  return (
    typeof start === 'string' &&
    typeof end === 'string' &&
    SCHEDULING_HHMM_RE.test(start) &&
    SCHEDULING_HHMM_RE.test(end) &&
    start < end
  )
}

/** Real calendar date in 'YYYY-MM-DD' form (rejects e.g. 2026-02-31) */
export function isValidSchedulingDateKey(value: string): boolean {
  if (!SCHEDULING_DATE_KEY_RE.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  )
}

export function isValidSchedulingDateOverrides(
  value: unknown,
): value is SchedulingDateOverrides {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  for (const [date, intervals] of Object.entries(
    value as Record<string, unknown>,
  )) {
    if (!isValidSchedulingDateKey(date)) return false
    if (!Array.isArray(intervals)) return false
    // Empty array is valid — it means unavailable all day.
    if (!intervals.every(interval => isValidSchedulingInterval(interval))) {
      return false
    }
  }
  return true
}

// ---------------------------------------------------------------------------
// Shared availability schedules (org-level named weekly hours, referenced by
// booking links and live-chat business hours)
// ---------------------------------------------------------------------------

export interface AvailabilityScheduleDTO {
  id: string
  name: string
  /** IANA timezone the weekly template is expressed in */
  timezone: string
  weekly: SchedulingAvailabilityTemplate
  /** Date-specific overrides ({} = none); see SchedulingDateOverrides */
  dateOverrides: SchedulingDateOverrides
  /** At most one per org; new booking links pre-select it */
  isDefault: boolean
  /** Booking links currently referencing this schedule (delete protection) */
  linkCount: number
  /** Whether the org's live-chat business hours reference this schedule */
  usedByLiveChat: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateAvailabilityScheduleDTO {
  name: string
  timezone: string
  weekly: SchedulingAvailabilityTemplate
  dateOverrides?: SchedulingDateOverrides
  isDefault?: boolean
}

export type UpdateAvailabilityScheduleDTO =
  Partial<CreateAvailabilityScheduleDTO>

export interface SchedulingLinkDTO {
  id: string
  name: string
  slug: string
  description: string | null
  durationMinutes: number
  bufferBeforeMinutes: number
  bufferAfterMinutes: number
  rollingDays: number
  minNoticeMinutes: number
  /** Inline fallback — the schedule's timezone wins when one is referenced */
  timezone: string
  /** Inline fallback — the schedule's weekly wins when one is referenced */
  availability: SchedulingAvailabilityTemplate
  /** Shared org schedule; null = custom (inline) hours */
  availabilityScheduleId: string | null
  /** Resolved for display — name of the referenced schedule */
  availabilityScheduleName: string | null
  locationType: SchedulingLocationType
  locationDetail: string | null
  /** Video service used when locationType is VIDEO. AUTO preserves legacy links. */
  videoProvider: SchedulingVideoProvider
  hostUserIds: string[]
  roundRobin: boolean
  /** Require hosts to be free on their connected calendars + Scrambl events */
  checkHostCalendars: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateSchedulingLinkDTO {
  name: string
  slug?: string
  description?: string | null
  durationMinutes: number
  bufferBeforeMinutes?: number
  bufferAfterMinutes?: number
  rollingDays?: number
  minNoticeMinutes?: number
  timezone: string
  availability: SchedulingAvailabilityTemplate
  /** Shared org schedule; null/absent = custom (inline) hours */
  availabilityScheduleId?: string | null
  locationType: SchedulingLocationType
  locationDetail?: string | null
  videoProvider?: SchedulingVideoProvider
  hostUserIds: string[]
  roundRobin?: boolean
  /** Require hosts to be free on their calendars (default true) */
  checkHostCalendars?: boolean
  isActive?: boolean
}

export type UpdateSchedulingLinkDTO = Partial<CreateSchedulingLinkDTO>

export interface SchedulingBookingDTO {
  id: string
  linkId: string
  linkName: string
  hostUserId: string
  hostName: string | null
  contactId: string | null
  inviteeName: string
  inviteeEmail: string
  inviteeNotes: string | null
  startTime: string
  endTime: string
  timezone: string
  status: SchedulingBookingStatus
  cancelledAt: string | null
  cancelReason: string | null
  createdAt: string
}

// ---------------------------------------------------------------------------
// Branding (org-configurable appearance of the public booking pages)
// ---------------------------------------------------------------------------

/** Internal (authenticated) branding settings, lazily created per org */
export interface SchedulingBrandingDTO {
  /** Hex accent color (e.g. '#4f46e5'); null = default accent */
  accentColor: string | null
  /** Custom logo URL override; wins over the org logo when set */
  logoUrl: string | null
  /** Fall back to the organization's logo when no custom logoUrl is set */
  useOrgLogo: boolean
  /** Public display name; null = organization name */
  brandName: string | null
  /** Short welcome paragraph shown under the booking page header */
  welcomeText: string | null
  /** Resolved organization logo URL (read-only, for the settings preview) */
  orgLogoUrl: string | null
  updatedAt: string
}

export interface UpdateSchedulingBrandingDTO {
  accentColor?: string | null
  logoUrl?: string | null
  useOrgLogo?: boolean
  brandName?: string | null
  welcomeText?: string | null
}

/** Resolved branding delivered to the public booking/cancel pages */
export interface PublicBookingBrandingDTO {
  /** Fully resolved: custom logo, else org logo (when useOrgLogo), else null */
  logoUrl: string | null
  /** Hex accent color; null = the page's default accent */
  accentColor: string | null
  /** Always resolved — brand name falls back to the organization name */
  brandName: string
  welcomeText: string | null
}

/** Public info shown on the booking page (no host ids leaked) */
export interface PublicBookingLinkInfoDTO {
  organizationName: string
  name: string
  slug: string
  description: string | null
  durationMinutes: number
  timezone: string
  locationType: SchedulingLocationType
  locationDetail: string | null
  videoProvider: SchedulingVideoProvider
  rollingDays: number
  branding: PublicBookingBrandingDTO
}

/** One bookable slot, UTC ISO strings */
export interface SchedulingSlotDTO {
  start: string
  end: string
}

export interface PublicCreateBookingDTO {
  /** UTC ISO start of the chosen slot */
  startTime: string
  /** Invitee's IANA timezone */
  timezone: string
  name: string
  email: string
  notes?: string
}

export interface PublicBookingConfirmationDTO {
  id: string
  startTime: string
  endTime: string
  timezone: string
  durationMinutes: number
  linkName: string
  organizationName: string
  locationType: SchedulingLocationType
  locationDetail: string | null
  meetingUrl: string | null
  meetingProvider: SchedulingVideoProvider | null
  cancelToken: string
}

/** Data for the public manage-booking page (cancel + reschedule) */
export interface PublicCancelInfoDTO {
  linkName: string
  organizationName: string
  inviteeName: string
  startTime: string
  endTime: string
  timezone: string
  status: SchedulingBookingStatus
  branding: PublicBookingBrandingDTO
  /** For the reschedule slot picker (loads the public slots endpoint) */
  orgSlug: string
  linkSlug: string
  durationMinutes: number
  locationType: SchedulingLocationType
  locationDetail: string | null
  meetingUrl: string | null
  meetingProvider: SchedulingVideoProvider | null
}

/** Move a booking to a new slot — atomic rebook-then-cancel, keyed on the
 *  cancel token. Result is a fresh PublicBookingConfirmationDTO (the new
 *  booking has its own cancel token). */
export interface PublicRescheduleBookingDTO {
  /** UTC ISO start of the newly chosen slot */
  startTime: string
  /** Invitee's IANA timezone */
  timezone: string
}
